import { router } from "@inertiajs/react"
import { Check, Circle, Rocket } from "lucide-react"
import { Button } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, } from "@/components/ui/dropdown-menu"
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar, } from "@/components/ui/sidebar"
import { cn } from "@/lib/utils"
import { t } from "@/lib/i18n"
import { patchInline, toastInlineUpdateError, toastInlineUpdateSuccess } from "@/lib/inline-update"
export type OnboardingProgressStep = {
    key: string
    label: string
    href: string
    done: boolean
}
export type SharedOnboardingProgress = {
    steps: OnboardingProgressStep[]
    completedCount: number
    totalCount: number
    progressPercentage: number
    readyForLaunch: boolean
}
type StaffOnboardingProgressProps = {
    progress: SharedOnboardingProgress
}
export function StaffOnboardingProgress({ progress }: StaffOnboardingProgressProps) {
    const { steps, completedCount, totalCount, progressPercentage, readyForLaunch } = progress
    const nextStep = steps.find((step) => !step.done)
    const nextStepLabel = nextStep ? nextStep.label : t("ui.react.components.layouts.staff_onboarding_progress.launch")
    // Reports itself like every other staff write. It used to be a bare
    // `router.patch`, which meant the server's answer -- "Ubicación en
    // producción." or the list of missing requirements -- reached the page as a
    // flash prop that nothing on this path read. The button only appears once
    // the checklist is full, so the refusal is rare; silent is still wrong.
    const launch = () => {
        patchInline("/manage/location/launch")
            .then((result) => toastInlineUpdateSuccess(result))
            .catch((error: unknown) => toastInlineUpdateError(error))
    }
    const { isMobile, state } = useSidebar()
    const collapsed = state === "collapsed" && !isMobile
    return (<SidebarMenu>
      <SidebarMenuItem>
      <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {/* Two lines rather than one: the navbar laid the label, the bar and the
            percentage out in a row, and that row does not fit a 224px panel. */}
        <SidebarMenuButton size="lg" data-testid="onboarding-progress-trigger" aria-label={t("ui.react.components.layouts.staff_onboarding_progress.open_menu")} tooltip={collapsed ? `${t("ui.react.components.layouts.staff_onboarding_progress.pending_setup")} · ${progressPercentage}%` : undefined}>
          <Rocket className="text-muted-foreground"/>
          <span className="grid min-w-0 flex-1 gap-1">
            <span className="truncate text-xs font-medium text-muted-foreground">{t("ui.react.components.layouts.staff_onboarding_progress.pending_setup")}</span>
            <span className="flex items-center gap-2">
              <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                <span className="block h-full rounded-full bg-primary transition-all" style={{ width: `${progressPercentage}%` }}/>
              </span>
              <span className="text-xs font-medium tabular-nums text-muted-foreground">{progressPercentage}%</span>
            </span>
          </span>
        </SidebarMenuButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent side={isMobile ? "top" : "right"} align="end" sideOffset={8} className="w-[min(18rem,calc(100vw-1rem))]">
        <DropdownMenuLabel>
          <p className="text-sm font-semibold">{t("ui.react.components.layouts.staff_onboarding_progress.title")}</p>
          <p className="text-xs font-normal text-muted-foreground">
            {t("ui.react.components.layouts.staff_onboarding_progress.steps_completed", { completed: completedCount, total: totalCount })}
          </p>
          <p className="text-xs font-medium text-brand-ink" data-testid="onboarding-next-step">
            {t("ui.react.components.layouts.staff_onboarding_progress.next_step", { step: nextStepLabel })}
          </p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {steps.map((step) => (<DropdownMenuItem key={step.key} data-testid={`onboarding-step-${step.key}`} onSelect={() => router.visit(step.href)}>
            {step.done ? (<Check className="size-4 text-success"/>) : (<Circle className="size-4 text-muted-foreground"/>)}
            <span className={cn("flex-1 truncate", step.done && "text-muted-foreground line-through")}>{step.label}</span>
          </DropdownMenuItem>))}
        {readyForLaunch ? (<>
            <DropdownMenuSeparator />
            <div className="p-1.5">
              <Button type="button" size="sm" className="w-full" data-testid="onboarding-launch-button" onClick={launch}>
                <Rocket className="size-4"/>{t("ui.react.components.layouts.staff_onboarding_progress.launch")}</Button>
            </div>
          </>) : null}
      </DropdownMenuContent>
    </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>)
}
